import { useState } from 'react';

const uid = () => Math.random().toString(36).slice(2, 8);

const Badge = ({ children, color = '#e8fdf2', textColor = '#128C7E', border = '#c3f0da' }) => (
  <span style={{
    fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
    background: color, color: textColor, border: `1px solid ${border}`
  }}>{children}</span>
);

const INTENT_ACTIONS = [
  { value: 'workflow', label: 'Trigger Workflow', icon: '🔄' },
  { value: 'text', label: 'Send Text Reply', icon: '💬' },
  { value: 'agent', label: 'Assign to Agent', icon: '👤' },
  { value: 'label', label: 'Add Label', icon: '🏷️' },
];

const SAMPLE_INTENTS = [
  {
    id: uid(), name: 'Pricing Inquiry', active: true, confidence: 75,
    description: 'Customer asks about cost, pricing, plans, fees',
    trainingPhrases: ['how much does it cost', 'what are your prices', 'pricing plans', 'monthly fee', 'is it free'],
    action: 'workflow', actionValue: 'Lead Qualification',
    stats: { triggered: 142, resolved: 118 }
  },
  {
    id: uid(), name: 'Support Request', active: true, confidence: 70,
    description: 'Customer needs help or reports a problem',
    trainingPhrases: ['I need help', 'not working', 'issue with', 'problem', 'error', 'broken'],
    action: 'agent', actionValue: 'Support Team',
    stats: { triggered: 89, resolved: 74 }
  },
  {
    id: uid(), name: 'Order Tracking', active: false, confidence: 80,
    description: 'Customer wants to know order or delivery status',
    trainingPhrases: ['where is my order', 'track my package', 'delivery status', 'when will it arrive'],
    action: 'workflow', actionValue: 'Order Status Check',
    stats: { triggered: 56, resolved: 54 }
  },
];

function IntentModal({ intent, onSave, onClose }) {
  const [data, setData] = useState(intent);
  const [newPhrase, setNewPhrase] = useState('');
  const upd = (k, v) => setData(d => ({ ...d, [k]: v }));

  const addPhrase = () => {
    const p = newPhrase.trim();
    if (p && !data.trainingPhrases.includes(p)) {
      upd('trainingPhrases', [...data.trainingPhrases, p]);
      setNewPhrase('');
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 16px', overflowY: 'auto'
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 600,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)', padding: 24
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
            {intent.name ? `Edit: ${intent.name}` : 'New Intent'}
          </h3>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af' }}>✕</button>
        </div>

        {/* Name */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Intent Name *</label>
          <input value={data.name} onChange={e => upd('name', e.target.value)}
            placeholder="e.g. Pricing Inquiry, Return Request" style={{
              width: '100%', padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: 8,
              fontSize: 14, boxSizing: 'border-box'
            }} />
        </div>

        {/* Description */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Description</label>
          <input value={data.description} onChange={e => upd('description', e.target.value)}
            placeholder="What customer intent does this represent?" style={{
              width: '100%', padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: 8,
              fontSize: 13, boxSizing: 'border-box'
            }} />
        </div>

        {/* Training Phrases */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
            Training Phrases <span style={{ color: '#9ca3af', fontWeight: 400 }}>(min 5 recommended)</span>
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {data.trainingPhrases.map((p, i) => (
              <span key={i} style={{
                background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8',
                borderRadius: 20, fontSize: 12, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 4
              }}>
                {p}
                <button onClick={() => upd('trainingPhrases', data.trainingPhrases.filter((_, j) => j !== i))}
                  style={{ border: 'none', background: 'none', color: '#93c5fd', cursor: 'pointer', fontSize: 12, padding: '0 2px' }}>✕</button>
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input value={newPhrase} onChange={e => setNewPhrase(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addPhrase()}
              placeholder="Type a phrase and press Enter or Add" style={{
                flex: 1, padding: '7px 12px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 13
              }} />
            <button onClick={addPhrase} style={{
              padding: '7px 14px', background: '#eff6ff', border: '1px solid #bfdbfe',
              color: '#1d4ed8', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer'
            }}>Add</button>
          </div>
        </div>

        {/* Confidence */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
            Confidence Threshold: <span style={{ color: '#25D366' }}>{data.confidence}%</span>
          </label>
          <input type="range" min={50} max={95} step={5} value={data.confidence}
            onChange={e => upd('confidence', Number(e.target.value))}
            style={{ width: '100%', accentColor: '#25D366' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
            <span>50% — broader matching</span>
            <span>95% — strict matching</span>
          </div>
        </div>

        {/* Action */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Action when triggered</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {INTENT_ACTIONS.map(a => (
              <button key={a.value} onClick={() => upd('action', a.value)} style={{
                padding: '6px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer', fontWeight: 500,
                border: `1.5px solid ${data.action === a.value ? '#25D366' : '#e0e0e0'}`,
                background: data.action === a.value ? '#e8fdf2' : '#fff',
                color: data.action === a.value ? '#128C7E' : '#555',
              }}>{a.icon} {a.label}</button>
            ))}
          </div>
          <input value={data.actionValue} onChange={e => upd('actionValue', e.target.value)}
            placeholder={
              data.action === 'workflow' ? 'Workflow name to trigger' :
              data.action === 'text' ? 'Auto-reply message text' :
              data.action === 'agent' ? 'Agent or team name' :
              'Label name'
            }
            style={{
              width: '100%', padding: '7px 12px', border: '1px solid #e0e0e0',
              borderRadius: 8, fontSize: 13, boxSizing: 'border-box'
            }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
          <button onClick={onClose} style={{
            padding: '9px 20px', border: '1px solid #e0e0e0', borderRadius: 8,
            background: '#fff', color: '#555', cursor: 'pointer', fontSize: 13
          }}>Cancel</button>
          <button onClick={() => onSave(data)} style={{
            padding: '9px 24px', border: 'none', borderRadius: 8,
            background: '#25D366', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer'
          }}>Save Intent</button>
        </div>
      </div>
    </div>
  );
}

function IntentCard({ intent, onToggle, onEdit, onDelete }) {
  const actionInfo = INTENT_ACTIONS.find(a => a.value === intent.action);
  const resolveRate = intent.stats.triggered > 0
    ? Math.round((intent.stats.resolved / intent.stats.triggered) * 100) : 0;

  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
      padding: '14px 18px', marginBottom: 10
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        {/* Toggle */}
        <button onClick={() => onToggle(intent.id)} style={{
          width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
          background: intent.active ? '#25D366' : '#ccc', position: 'relative', transition: '0.2s', flexShrink: 0, marginTop: 2
        }}>
          <span style={{
            position: 'absolute', top: 3, left: intent.active ? 22 : 3, width: 18, height: 18,
            borderRadius: '50%', background: '#fff', transition: '0.2s', display: 'block'
          }} />
        </button>

        {/* Main info */}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>{intent.name}</span>
            <Badge color="#f5f3ff" textColor="#6d28d9" border="#ddd6fe">
              {intent.confidence}% confidence
            </Badge>
            <Badge>{actionInfo?.icon} {actionInfo?.label}</Badge>
            {intent.actionValue && (
              <Badge color="#eff6ff" textColor="#1d4ed8" border="#bfdbfe">→ {intent.actionValue}</Badge>
            )}
          </div>
          <div style={{ color: '#6b7280', fontSize: 12, marginTop: 3 }}>{intent.description}</div>

          {/* Training phrases preview */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
            {intent.trainingPhrases.slice(0, 4).map((p, i) => (
              <span key={i} style={{
                background: '#f3f4f6', color: '#374151', fontSize: 11,
                padding: '2px 8px', borderRadius: 10
              }}>"{p}"</span>
            ))}
            {intent.trainingPhrases.length > 4 && (
              <span style={{ color: '#9ca3af', fontSize: 11, padding: '2px 0' }}>
                +{intent.trainingPhrases.length - 4} more
              </span>
            )}
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              <strong style={{ color: '#374151' }}>{intent.stats.triggered}</strong> triggered
            </span>
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              <strong style={{ color: '#128C7E' }}>{resolveRate}%</strong> resolved
            </span>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => onEdit(intent)} style={{
            padding: '6px 12px', border: '1px solid #e0e0e0', borderRadius: 8,
            background: '#fff', color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer'
          }}>✏️ Edit</button>
          <button onClick={() => onDelete(intent.id)} style={{
            padding: '6px 10px', border: '1px solid #fee2e2', borderRadius: 8,
            background: '#fff', color: '#ef4444', fontSize: 12, cursor: 'pointer'
          }}>🗑</button>
        </div>
      </div>
    </div>
  );
}

const emptyIntent = () => ({
  id: uid(), name: '', active: false, confidence: 70, description: '',
  trainingPhrases: [], action: 'workflow', actionValue: '',
  stats: { triggered: 0, resolved: 0 }
});

export default function AIIntent() {
  const [intents, setIntents] = useState(SAMPLE_INTENTS);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [globalEnabled, setGlobalEnabled] = useState(true);

  const filtered = intents.filter(i =>
    i.name.toLowerCase().includes(search.toLowerCase()) ||
    i.description.toLowerCase().includes(search.toLowerCase())
  );

  const handleToggle = (id) =>
    setIntents(is => is.map(i => i.id === id ? { ...i, active: !i.active } : i));

  const handleSave = (intent) => {
    setIntents(is => is.some(i => i.id === intent.id) ? is.map(i => i.id === intent.id ? intent : i) : [...is, intent]);
    setEditing(null);
  };

  const handleDelete = (id) => {
    if (window.confirm('Delete this intent?')) setIntents(is => is.filter(i => i.id !== id));
  };

  const activeCount = intents.filter(i => i.active).length;

  return (
    <div>
      {/* Info banner */}
      <div style={{
        background: '#fdf4ff', border: '1px solid #e9d5ff', borderRadius: 10,
        padding: '10px 14px', fontSize: 13, color: '#6b21a8', marginBottom: 16
      }}>
        ✨ <strong>AI Intent Matching</strong> triggers workflows based on <strong>customer intent</strong>, not exact keywords. The AI understands meaning and context — more powerful than Custom Auto Reply.
      </div>

      {/* Global toggle + toolbar */}
      <div style={{
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
        padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12
      }}>
        <button onClick={() => setGlobalEnabled(v => !v)} style={{
          width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
          background: globalEnabled ? '#25D366' : '#ccc', position: 'relative', transition: '0.2s', flexShrink: 0
        }}>
          <span style={{
            position: 'absolute', top: 3, left: globalEnabled ? 22 : 3, width: 18, height: 18,
            borderRadius: '50%', background: '#fff', transition: '0.2s', display: 'block'
          }} />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>AI Intent Matching — {globalEnabled ? 'Active' : 'Paused'}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>{activeCount} of {intents.length} intents enabled</div>
        </div>
      </div>

      {/* Search + Add */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 14 }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search intents…" style={{
              width: '100%', padding: '8px 12px 8px 32px', border: '1px solid #e0e0e0',
              borderRadius: 8, fontSize: 13, boxSizing: 'border-box'
            }} />
        </div>
        <span style={{ fontSize: 13, color: '#6b7280', whiteSpace: 'nowrap' }}>
          {intents.length} intent{intents.length !== 1 ? 's' : ''}
        </span>
        <button onClick={() => setEditing(emptyIntent())} style={{
          padding: '8px 16px', background: '#7c3aed', color: '#fff', border: 'none',
          borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap'
        }}>+ New Intent</button>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '48px 20px', color: '#9ca3af',
          background: '#f9fafb', borderRadius: 12, border: '1.5px dashed #e5e7eb'
        }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>✨</div>
          <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>No intents configured</div>
          <div style={{ fontSize: 13 }}>Add training phrases to let AI understand what customers mean.</div>
        </div>
      ) : (
        filtered.map(intent => (
          <IntentCard key={intent.id} intent={intent}
            onToggle={handleToggle}
            onEdit={setEditing}
            onDelete={handleDelete} />
        ))
      )}

      {editing && (
        <IntentModal intent={editing} onSave={handleSave} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}